import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import LayoutSkeleton from './Layout/LayoutSkeleton';

const OrganizationProfileGuard = ({ children }) => {
  const { user } = useAuth();
  const api = useApi();
  const [isProfileComplete, setIsProfileComplete] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasCheckedRef = useRef(false);
  const checkInProgressRef = useRef(false);

  useEffect(() => {
    const checkOrganizationProfile = async () => {
      // Skip if already checked
      if (hasCheckedRef.current) {
        setLoading(false);
        return;
      }

      // Skip if check in progress or no user
      if (checkInProgressRef.current || !user) {
        if (!user) {
          setLoading(false);
        }
        return;
      }

      checkInProgressRef.current = true;

      try {
        console.log('🔍 [ORG PROFILE GUARD] Starting profile check', {
          userId: user?.id,
          userEmail: user?.email,
          userRole: user?.role
        });
        
        // First check billing profile to see if bypass is enabled
        const billingResponse = await api.get('/api/v1/billing/profile');
        const billingData = billingResponse?.data?.data;
        
        console.log('🔍 [ORG PROFILE GUARD] Billing data received', {
          paymentBypassEnabled: billingData?.paymentBypassEnabled,
          orgPlanSelection: billingData?.orgPlanSelection,
          subscriptionStatus: billingData?.subscriptionStatus,
          userIsActivated: billingData?.userIsActivated,
          fullData: billingData
        });
        
        // If payment bypass is enabled, skip profile check
        if (billingData?.paymentBypassEnabled === true) {
          console.log('✅ [ORG PROFILE GUARD] Payment bypass enabled - skipping profile check', {
            paymentBypassEnabled: billingData.paymentBypassEnabled,
            orgPlanSelection: billingData.orgPlanSelection
          });
          setIsProfileComplete(true);
          hasCheckedRef.current = true;
          setLoading(false);
          checkInProgressRef.current = false;
          return;
        }
        
        console.log('⚠️ [ORG PROFILE GUARD] Payment bypass NOT enabled, checking profile', {
          paymentBypassEnabled: billingData?.paymentBypassEnabled
        });
        
        // Use different endpoint based on user role
        const endpoint = user.role === 'org_user' 
          ? '/api/v1/organizations/basic-info' 
          : '/api/v1/organizations/profile';
        
        const response = await api.get(endpoint);
        const organization = response?.data?.data?.organization;
        
        console.log('🔍 [ORG PROFILE GUARD] Organization profile data', {
          orgName: organization?.name,
          orgEmail: organization?.email,
          hasName: !!organization?.name,
          hasEmail: !!organization?.email
        });
        
        // Check if organization profile is complete
        // Only require essential fields: name and email
        // Phone and industry are optional and shouldn't block dashboard access
        const isComplete = organization?.name && organization?.email;
        
        console.log('🔍 [ORG PROFILE GUARD] Profile completeness check', {
          isComplete,
          reason: !isComplete ? (!organization?.name ? 'Missing name' : 'Missing email') : 'Complete'
        });
        
        setIsProfileComplete(isComplete);
        hasCheckedRef.current = true;
      } catch (error) {
        console.error('❌ [ORG PROFILE GUARD] Error checking organization profile:', {
          error: error.message,
          response: error.response?.data,
          status: error.response?.status,
          stack: error.stack
        });
        // If there's an error, assume profile is incomplete
        setIsProfileComplete(false);
        hasCheckedRef.current = true;
      } finally {
        setLoading(false);
        checkInProgressRef.current = false;
        console.log('🔍 [ORG PROFILE GUARD] Profile check completed', {
          isProfileComplete,
          loading: false
        });
      }
    };

    checkOrganizationProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]); // Only depend on user.id and user.role - api is stable via useMemo

  // If user is not authenticated, let the ProtectedRoute handle it
  if (!user) {
    return children;
  }

  // Show layout skeleton while checking organization profile
  // This is the actual layout skeleton that matches the dashboard structure
  // Only show on initial check, not on navigation
  if (loading && !hasCheckedRef.current) {
    return <LayoutSkeleton />;
  }

  // If organization profile is not complete, redirect to organization profile setup
  // Only redirect if we've actually checked and it's incomplete
  if (hasCheckedRef.current && isProfileComplete === false) {
    console.log('⚠️ [ORG PROFILE GUARD] Profile incomplete - redirecting to settings', {
      isProfileComplete,
      userEmail: user?.email
    });
    return <Navigate to="/dashboard/settings?section=organization" replace />;
  }

  console.log('✅ [ORG PROFILE GUARD] Profile complete - rendering children', {
    isProfileComplete,
    userEmail: user?.email,
    hasChecked: hasCheckedRef.current
  });

  // If profile is complete or not yet checked, render the children
  // This prevents blocking navigation while the check is happening
  return children;
};

export default OrganizationProfileGuard;
